<template>
  <div class="approval-page pa-4 pa-md-5 animate-fade-in">
    <!-- Gradient Header -->
    <v-card
      elevation="12"
      class="header-card mb-5 overflow-hidden animate-slide-down"
    >
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-5 pa-sm-6">
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="8" class="d-flex align-center">
              <div class="header-icon-box">
                <v-icon size="40px" color="white">mdi-school</v-icon>
              </div>
              <div class="ml-4">
                <h1 class="header-title white--text mb-1">
                  Student Scholarship Preview
                </h1>
                <p class="header-subtitle white--text mb-0">
                  Review and approve student scholarship application
                </p>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="d-flex justify-md-end mt-3 mt-md-0">
              <v-btn class="back-btn" rounded @click="goBack">
                <v-icon left>mdi-arrow-left</v-icon> Back to List
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Loading -->
    <v-overlay :value="LoadingFlag">
      <v-progress-circular
        indeterminate
        size="64"
        color="#0097D7"
      ></v-progress-circular>
    </v-overlay>

    <!-- Stats Cards Row -->
    <v-row class="mb-4 animate-slide-up delay-1">
      <!-- Zone Data Card -->
      <v-col cols="12" md="6" class="py-2">
        <v-card
          class="section-card fill-height zone-card position-relative overflow-hidden"
        >
          <div class="section-header zone-header">
            <v-icon color="white" class="mr-2" size="28"
              >mdi-map-marker-radius</v-icon
            >
            <span class="section-header-title zone-title"
              >Zone Wise Data ({{ records.ZoneName || "N/A" }})</span
            >
          </div>
          <!-- Watermark Icon -->
          <v-icon class="watermark-icon zone-watermark">mdi-earth</v-icon>

          <v-card-text class="px-5 py-4 card-content-relative">
            <v-row>
              <v-col cols="12" sm="6" class="py-2">
                <div
                  class="font-weight-bold mb-3 sub-section-title zone-sub-title"
                >
                  <v-icon size="18" color="#56BDA3" class="mr-1"
                    >mdi-trending-up</v-icon
                  >
                  Foundation Contribution
                </div>
                <div
                  v-for="contrib in records.ZoneContributions"
                  :key="contrib.Year"
                  class="zone-contribution-row"
                >
                  <span class="detail-label-sub">Year {{ contrib.Year }}</span>
                  <span class="detail-value-highlight"
                    >₹ {{ contrib.Amount.toLocaleString("en-IN") }}</span
                  >
                </div>
              </v-col>
              <v-col cols="12" sm="6" class="py-2">
                <div
                  class="font-weight-bold mb-3 sub-section-title zone-sub-title"
                >
                  <v-icon size="18" color="#56BDA3" class="mr-1"
                    >mdi-file-document-outline</v-icon
                  >
                  Scholarship Applications
                </div>
                <div class="zone-stat-box total">
                  <span class="detail-label-sub">
                    <v-icon size="16" color="#1e3a34" class="mr-1"
                      >mdi-file-document</v-icon
                    >
                    Total Applications
                  </span>
                  <span class="stat-badge-zone-total">
                    <animated-number
                      :value="records.ZoneTotalApplications || 0"
                    />
                  </span>
                </div>
                <div class="zone-stat-box approved">
                  <span class="detail-label-sub">
                    <v-icon size="16" color="#56BDA3" class="mr-1"
                      >mdi-check-circle-outline</v-icon
                    >
                    Approved
                  </span>
                  <span class="stat-badge-seaform">
                    <animated-number
                      :value="records.ZoneApprovedApplications || 0"
                    />
                  </span>
                </div>
                <div class="zone-stat-box rejected">
                  <span class="detail-label-sub">
                    <v-icon size="16" color="#EDBE38" class="mr-1"
                      >mdi-alert-circle-outline</v-icon
                    >
                    Rejected
                  </span>
                  <span class="stat-badge-gold">
                    <animated-number
                      :value="records.ZoneRejectedApplications || 0"
                    />
                  </span>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- LO Data Card -->
      <v-col cols="12" md="6" class="py-2">
        <v-card
          class="section-card fill-height lo-card position-relative overflow-hidden"
        >
          <div class="section-header lo-header">
            <v-icon color="white" class="mr-2" size="28"
              >mdi-office-building</v-icon
            >
            <span class="section-header-title lo-title"
              >LO Wise Data ({{ records.LomName || "N/A" }})</span
            >
          </div>
          <!-- Watermark Icon -->
          <v-icon class="watermark-icon lo-watermark"
            >mdi-office-building</v-icon
          >

          <v-card-text class="px-5 py-4 card-content-relative">
            <v-row>
              <v-col cols="12" sm="6" class="py-2">
                <div
                  class="font-weight-bold mb-3 sub-section-title lo-sub-title"
                >
                  <v-icon size="18" color="#edbe38" class="mr-1"
                    >mdi-trending-up</v-icon
                  >
                  Foundation Contribution
                </div>
                <div
                  v-for="contrib in records.LoContributions"
                  :key="contrib.Year"
                  class="lo-contribution-row"
                >
                  <span class="detail-label-sub white-text-compat"
                    >Year {{ contrib.Year }}</span
                  >
                  <span class="detail-value-highlight white-text-compat"
                    >₹ {{ contrib.Amount.toLocaleString("en-IN") }}</span
                  >
                </div>
              </v-col>
              <v-col cols="12" sm="6" class="py-2">
                <div
                  class="font-weight-bold mb-3 sub-section-title lo-sub-title"
                >
                  <v-icon size="18" color="#edbe38" class="mr-1"
                    >mdi-file-document-outline</v-icon
                  >
                  Scholarship Applications
                </div>
                <div class="lo-stat-box total">
                  <span class="detail-label-sub white-text-compat">
                    <v-icon size="16" color="#78350f" class="mr-1"
                      >mdi-file-document</v-icon
                    >
                    Total Applications
                  </span>
                  <span class="stat-badge-lo-total">
                    <animated-number
                      :value="records.LoTotalApplications || 0"
                    />
                  </span>
                </div>
                <div class="lo-stat-box approved">
                  <span class="detail-label-sub white-text-compat">
                    <v-icon size="16" color="#56bda3" class="mr-1"
                      >mdi-check-circle-outline</v-icon
                    >
                    Approved
                  </span>
                  <span class="stat-badge-seaform">
                    <animated-number
                      :value="records.LoApprovedApplications || 0"
                    />
                  </span>
                </div>
                <div class="lo-stat-box rejected">
                  <span class="detail-label-sub white-text-compat">
                    <v-icon size="16" color="#edbe38" class="mr-1"
                      >mdi-alert-circle-outline</v-icon
                    >
                    Rejected
                  </span>
                  <span class="stat-badge-gold">
                    <animated-number
                      :value="records.LoRejectedApplications || 0"
                    />
                  </span>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Student Details + Bank Details Row -->
    <v-row class="mb-4 animate-slide-up delay-2">
      <!-- Student Details -->
      <v-col cols="12" md="8" class="py-2">
        <v-card class="section-card fill-height">
          <div class="section-header standard-header">
            <v-icon color="white" class="mr-2" size="26"
              >mdi-account-circle</v-icon
            >
            <span class="section-header-title standard-header-title"
              >Student Details</span
            >
          </div>
          <v-card-text class="px-5 py-4">
            <v-row align="center">
              <v-col cols="12" sm="3" class="text-center py-2">
                <v-img
                  :src="records.StudentImagePath"
                  height="140"
                  contain
                  class="student-img mx-auto"
                ></v-img>
              </v-col>
              <v-col cols="12" sm="9" class="py-2">
                <v-row>
                  <v-col cols="12" sm="6" class="py-2">
                    <div class="detail-item">
                      <span class="detail-label">Application No</span>
                      <span class="detail-value">{{
                        records.ApplicationNumber
                      }}</span>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" class="py-2">
                    <div class="detail-item">
                      <span class="detail-label">Name</span>
                      <span class="detail-value">{{
                        records.StudentName
                      }}</span>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" class="py-2">
                    <div class="detail-item">
                      <span class="detail-label">Gender</span>
                      <span class="detail-value">{{ records.Gender }}</span>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" class="py-2">
                    <div class="detail-item">
                      <span class="detail-label">School</span>
                      <span class="detail-value text-right-align">{{
                        records.StudentSchoolName
                      }}</span>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" class="py-2">
                    <div class="detail-item">
                      <span class="detail-label">Parent Name</span>
                      <span class="detail-value">{{
                        records.StudentParentName
                      }}</span>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" class="py-2">
                    <div class="detail-item">
                      <span class="detail-label">Parent Contact</span>
                      <span class="detail-value">{{
                        records.ParentContact
                      }}</span>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" class="py-2">
                    <div class="detail-item">
                      <span class="detail-label">Category</span>
                      <span class="detail-value">{{ records.Category }}</span>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" class="py-2">
                    <div class="detail-item">
                      <span class="detail-label">Sponsor Name</span>
                      <span class="detail-value">{{
                        records.SponserName
                      }}</span>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" class="py-2">
                    <div class="detail-item">
                      <span class="detail-label">Scholarship Type</span>
                      <v-chip
                        medium
                        :color="records.StudentScholarshipTypeColor"
                        class="white--text font-weight-bold chip-text-large"
                        >{{ records.StudentScholarshipTypeTxt }}</v-chip
                      >
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" class="py-2">
                    <div class="detail-item">
                      <span class="detail-label">Approval Status</span>
                      <v-chip
                        medium
                        :color="records.StudentApprovalStatusColor"
                        class="white--text font-weight-bold chip-text-large"
                        >{{ records.StudentApprovalStatusTxt }}</v-chip
                      >
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" class="py-2">
                    <div class="detail-item">
                      <span class="detail-label">Application Status</span>
                      <v-chip
                        medium
                        :color="records.ReportStatusColor"
                        class="white--text font-weight-bold chip-text-large"
                        >{{ records.ReportStatusTxt }}</v-chip
                      >
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Bank Details -->
      <v-col cols="12" md="4" class="py-2">
        <v-card class="section-card fill-height">
          <div class="section-header standard-header">
            <v-icon color="white" class="mr-2" size="26">mdi-bank</v-icon>
            <span class="section-header-title standard-header-title"
              >Bank Details</span
            >
          </div>
          <v-card-text class="px-5 py-4">
            <v-row>
              <v-col cols="12" class="py-2">
                <div class="detail-item">
                  <span class="detail-label">Account Holder</span>
                  <span class="detail-value">{{
                    records.AccountHolderName
                  }}</span>
                </div>
              </v-col>
              <v-col cols="12" class="py-2">
                <div class="detail-item">
                  <span class="detail-label">Account Number</span>
                  <span class="detail-value">{{ records.AccountNumber }}</span>
                </div>
              </v-col>
              <v-col cols="12" class="py-2">
                <div class="detail-item">
                  <span class="detail-label">Bank Name</span>
                  <span class="detail-value">{{ records.BankName }}</span>
                </div>
              </v-col>
              <v-col cols="12" class="py-2">
                <div class="detail-item">
                  <span class="detail-label">Branch Name</span>
                  <span class="detail-value">{{ records.BranchName }}</span>
                </div>
              </v-col>
              <v-col cols="12" class="py-2">
                <div class="detail-item">
                  <span class="detail-label">IFSC Code</span>
                  <span class="detail-value">{{ records.IfscCode }}</span>
                </div>
              </v-col>
              <v-col cols="12" class="py-2">
                <div class="detail-item">
                  <span class="detail-label">Account Status</span>
                  <v-chip
                    medium
                    :color="records.AccountStatusTxtColor"
                    class="white--text font-weight-bold chip-text-large"
                    >{{ records.AccountStatusTxt }}</v-chip
                  >
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Mark Details + Transaction Details Row -->
    <v-row class="mb-4 animate-slide-up delay-3">
      <!-- Mark Details -->
      <v-col cols="12" md="4" class="py-2">
        <v-card class="section-card fill-height">
          <div class="section-header standard-header">
            <v-icon color="white" class="mr-2" size="26">mdi-school</v-icon>
            <span class="section-header-title standard-header-title"
              >Mark Details</span
            >
          </div>
          <v-card-text class="px-5 py-4">
            <v-row>
              <v-col cols="12" class="py-2">
                <div class="detail-item">
                  <span class="detail-label">Class</span>
                  <span class="detail-value"
                    >{{ records.ClassName }} th std</span
                  >
                </div>
              </v-col>
              <v-col cols="12" class="py-2">
                <div class="detail-item">
                  <span class="detail-label">Percentage</span>
                  <span class="detail-value">{{ records.Percentage }}%</span>
                </div>
              </v-col>
              <v-col cols="12" class="py-2">
                <div class="detail-item">
                  <span class="detail-label">Student Type</span>
                  <span class="detail-value">{{ records.StudentType }}</span>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Transaction Details -->
      <v-col cols="12" md="8" class="py-2">
        <v-card class="section-card fill-height">
          <div class="section-header standard-header">
            <v-icon color="white" class="mr-2" size="26">mdi-history</v-icon>
            <span class="section-header-title standard-header-title"
              >Transaction Details</span
            >
          </div>
          <v-card-text class="pa-0">
            <v-simple-table class="transaction-table">
              <thead>
                <tr>
                  <th class="font-weight-bold">Transaction Type</th>
                  <th class="font-weight-bold">Issued Date</th>
                  <th class="font-weight-bold">Issued Amount</th>
                  <th class="font-weight-bold">Remarks</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in records.ScholarshipIssued"
                  :key="index"
                >
                  <td class="font-weight-medium">
                    {{ item.TransactionTypeName }}
                  </td>
                  <td>{{ item.ScholarshipIssuedDate }}</td>
                  <td class="font-weight-bold text-no-wrap">
                    {{ item.ScholarshipIssuedAmount }} Rs
                  </td>
                  <td>{{ item.ScholarshipIssuedRemarks }}</td>
                </tr>
                <tr
                  v-if="
                    !records.ScholarshipIssued ||
                    records.ScholarshipIssued.length === 0
                  "
                >
                  <td colspan="4" class="text-center py-5 text-muted">
                    -- NA --
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Attachments -->
    <v-card class="section-card mb-4 animate-slide-up delay-4">
      <div class="section-header standard-header">
        <v-icon color="white" class="mr-2" size="26">mdi-attachment</v-icon>
        <span class="section-header-title standard-header-title"
          >Attachments</span
        >
      </div>
      <v-card-text class="px-5 py-4">
        <div v-viewer>
          <v-row>
            <v-col cols="12" sm="6" md="3" class="text-center py-3">
              <div class="attachment-title mb-2">Income Proof</div>
              <div class="img-container">
                <img
                  :src="records.IncomeProofImagePath"
                  class="attachment-img"
                />
              </div>
              <div class="mt-3">
                <a
                  :href="records.IncomeProofImagePath"
                  :download="records.IncomeProofImagePath"
                  target="_blank"
                  class="download-link"
                >
                  <v-icon color="#3A67B1" size="26">mdi-download</v-icon>
                </a>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="text-center py-3">
              <div class="attachment-title mb-2">Passbook</div>
              <div class="img-container">
                <img :src="records.PassbookImagePath" class="attachment-img" />
              </div>
              <div class="mt-3">
                <a
                  :href="records.PassbookImagePath"
                  :download="records.PassbookImagePath"
                  target="_blank"
                  class="download-link"
                >
                  <v-icon color="#3A67B1" size="26">mdi-download</v-icon>
                </a>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="text-center py-3">
              <div class="attachment-title mb-2">Marksheet</div>
              <div class="img-container">
                <img :src="records.MarksheetImagePath" class="attachment-img" />
              </div>
              <div class="mt-3">
                <a
                  :href="records.MarksheetImagePath"
                  :download="records.MarksheetImagePath"
                  target="_blank"
                  class="download-link"
                >
                  <v-icon color="#3A67B1" size="26">mdi-download</v-icon>
                </a>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>

    <!-- Previous Description -->
    <v-card
      class="section-card mb-4 animate-slide-up delay-4"
      v-if="records.DescriptionTxt && records.DescriptionTxt !== ''"
    >
      <div class="section-header standard-header">
        <v-icon color="white" class="mr-2" size="26">mdi-message</v-icon>
        <span class="section-header-title standard-header-title"
          >Description</span
        >
      </div>
      <v-card-text class="px-5 py-4">
        <p class="mb-0 description-text" v-html="records.DescriptionTxt"></p>
      </v-card-text>
    </v-card>

    <!-- Approval Remarks -->
    <v-card class="section-card mb-4 animate-slide-up delay-4">
      <div class="section-header standard-header">
        <v-icon color="white" class="mr-2" size="26"
          >mdi-comment-text-multiple-outline</v-icon
        >
        <span class="section-header-title standard-header-title"
          >Approval Remarks
          <span style="color: #edbe38; font-weight: bold">*</span></span
        >
      </div>
      <v-card-text class="px-5 py-4">
        <v-textarea
          outlined
          v-model="Description"
          :rules="DescriptionRules"
          label="Enter remarks / description"
          dense
          hide-details="auto"
          class="remarks-textarea"
        ></v-textarea>
      </v-card-text>
    </v-card>

    <!-- Action Buttons -->
    <v-row justify="center" class="mb-5 animate-slide-up delay-4">
      <v-col
        cols="12"
        class="d-flex flex-wrap justify-center align-center"
        style="gap: 20px"
      >
        <v-btn
          @click.prevent="submitForm(1)"
          :loading="ScholarshipLoadingFlag"
          color="#56BDA3"
          elevation="3"
          rounded
          large
          class="white--text action-btn font-weight-bold"
        >
          <v-icon left size="22">mdi-check-circle</v-icon> Approve for
          Scholarship
        </v-btn>
        <v-btn
          @click.prevent="submitForm(2)"
          :loading="EmpowerHerLoadingFlag"
          color="#0097D7"
          elevation="3"
          rounded
          large
          class="white--text action-btn font-weight-bold"
        >
          <v-icon left size="22">mdi-check-circle</v-icon> Approve for Empower
          Her
        </v-btn>
        <v-btn
          @click.prevent="submitForm(3)"
          :loading="RejectLoadingFlag"
          color="#EDBE38"
          elevation="3"
          rounded
          large
          class="action-btn font-weight-bold off-black-text"
        >
          <v-icon left size="22" color="#0A0F29">mdi-close-circle</v-icon>
          Reject
        </v-btn>
        <v-btn
          @click.prevent="goBack"
          color="#3A67B1"
          elevation="3"
          rounded
          large
          class="white--text action-btn font-weight-bold"
        >
          <v-icon left size="22">mdi-close-circle</v-icon> Cancel
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";

const AnimatedNumber = {
  name: "AnimatedNumber",
  props: {
    value: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      displayValue: 0,
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.animateValue(newVal);
      },
    },
  },
  methods: {
    animateValue(target) {
      const duration = 800; // ms
      const start = this.displayValue;
      const end = parseInt(target) || 0;
      if (start === end) return;

      const startTime = performance.now();

      const update = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = progress * (2 - progress); // easeOutQuad
        this.displayValue = Math.round(start + (end - start) * ease);

        if (progress < 1) {
          requestAnimationFrame(update);
        } else {
          this.displayValue = end;
        }
      };

      requestAnimationFrame(update);
    },
  },
  render(h) {
    return h("span", {}, this.displayValue);
  },
};

export default {
  name: "StudentScholarshipApprovalPage",
  mixins: [common],
  components: {
    AnimatedNumber,
  },
  data() {
    return {
      LoadingFlag: false,
      records: {},
      Description: "",
      DescriptionRules: [(v) => !!v || "Description is required"],
      ScholarshipLoadingFlag: false,
      EmpowerHerLoadingFlag: false,
      RejectLoadingFlag: false,
      ResultFlag: false,
      flag: false,
    };
  },
  methods: {
    goBack() {
      this.$router.push("/validate-applications/list");
    },
    getStudentDetails() {
      var StudentId = this.$route.query.studentId;
      if (!StudentId) return;

      this.LoadingFlag = true;
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;

      const thisIns = this;
      this.$http({
        url: server_url + "api/student/show",
        method: "POST",
        data: { UserInterface: 2, Student: StudentId },
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.LoadingFlag = false;
          if (response.data.flag == 1) {
            thisIns.records = response.data.records;
          } else {
            thisIns.sweetAlert("error", response.data.output, false);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.LoadingFlag = false;
        });
    },
    submitForm(Status) {
      var StudentId = this.$route.query.studentId;
      var ApplicationNumber = this.$route.query.applicationNumber;

      if (Status > 0) {
        if (Status === 1) this.ScholarshipLoadingFlag = true;
        else if (Status === 2) this.EmpowerHerLoadingFlag = true;
        else if (Status === 3) this.RejectLoadingFlag = true;

        this.LoadingFlag = true;
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;

        const thisIns = this;
        this.$http({
          url: server_url + "api/student/update-approval-status",
          method: "POST",
          data: {
            UserInterface: Status,
            Student: StudentId,
            Description: this.Description,
            ApplicationNumber: ApplicationNumber,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then(function (response) {
            thisIns.LoadingFlag = false;
            thisIns.ScholarshipLoadingFlag = false;
            thisIns.EmpowerHerLoadingFlag = false;
            thisIns.RejectLoadingFlag = false;
            if (response.data.flag == 1) {
              thisIns.sweetAlert("success", response.data.output, false);
              sessionStorage.setItem("validateApplicationsNeedsRefresh", "1");
              thisIns.goBack();
            } else {
              thisIns.sweetAlert("error", response.data.output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
            thisIns.ScholarshipLoadingFlag = false;
            thisIns.EmpowerHerLoadingFlag = false;
            thisIns.RejectLoadingFlag = false;
          });
      } else {
        this.sweetAlert("error", "Kindly fill the required fields", false);
      }
    },
  },
  beforeMount() {
    this.getStudentDetails();
  },
};
</script>

<style scoped>
.approval-page {
  min-height: 100vh;
  background-color: #f4f6f9;
  font-family: "Inter", "Roboto", sans-serif;
  color: #414042;
}

/* Header Card styles */
.header-card {
  background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%) !important;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  border: none;
  box-shadow: 0 12px 32px rgba(58, 103, 177, 0.25) !important;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.header-card::after {
  content: "";
  position: absolute;
  top: 0;
  left: -150%;
  width: 60%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.3) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: skewX(-25deg);
  animation: shine 7s infinite ease-in-out;
  pointer-events: none;
}

.header-card:hover {
  box-shadow: 0 16px 40px rgba(58, 103, 177, 0.4) !important;
  transform: translateY(-3px);
}

@keyframes shine {
  0% {
    left: -150%;
  }
  20% {
    left: 150%;
  }
  100% {
    left: 150%;
  }
}

.header-gradient {
  position: relative;
  z-index: 1;
}

.header-backdrop-effect {
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.12) 0%,
    transparent 70%
  );
  pointer-events: none;
}

.header-content {
  position: relative;
  z-index: 2;
}

.header-icon-box {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(12px);
  border-radius: 12px;
  padding: 14px;
  border: 1.5px solid rgba(255, 255, 255, 0.25);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.header-title {
  font-size: 2.3rem;
  font-weight: 800;
  color: #ffffff !important;
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.header-subtitle {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9) !important;
  font-weight: 500;
  margin-top: 4px;
}

.back-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  border: 1.5px solid rgba(255, 255, 255, 0.3) !important;
  color: #ffffff !important;
  font-size: 1.2rem !important;
  font-weight: 700 !important;
  text-transform: none !important;
  height: 48px !important;
  padding: 0 24px !important;
  border-radius: 24px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
}

.back-btn:hover {
  background: #ffffff !important;
  color: #3a67b1 !important;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12) !important;
  transform: translateX(-4px);
}

/* Cards layout & styles */
.section-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  box-shadow: 0 6px 16px rgba(10, 15, 41, 0.04) !important;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  border-left: 5px solid #3a67b1 !important; /* Standard Navy left border */
}

.section-card:hover {
  transform: translateY(-6px) scale(1.012);
  box-shadow: 0 18px 36px rgba(10, 15, 41, 0.12) !important;
  border-color: #3a67b1;
}

.fill-height {
  height: 100%;
}

.section-header {
  background: #ffffff;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  border-bottom: 1.5px solid #edf2f7;
}

.section-header-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #0a0f29;
  letter-spacing: 0.2px;
}

/* Standard Header and Title Overrides */
.standard-header {
  background: linear-gradient(90deg, #3a67b1 0%, #0097d7 100%) !important;
  border-bottom: none !important;
  padding: 14px 20px !important;
}

.standard-header-title {
  color: #ffffff !important;
  font-weight: 700 !important;
}

/* Zone Card Design (Instantly Distinguishable) */
.zone-card {
  border-left: 8px solid #56bda3 !important;
  border-right: 1px solid #e2e8f0 !important;
  border-top: 1px solid #e2e8f0 !important;
  border-bottom: 1px solid #e2e8f0 !important;
  background: linear-gradient(
    135deg,
    rgba(86, 189, 163, 0.12) 0%,
    rgba(255, 255, 255, 0.95) 100%
  ) !important;
  position: relative !important;
}
.zone-card:hover {
  border-color: #56bda3 !important;
  transform: translateY(-8px) scale(1.02) !important;
  box-shadow: 0 20px 40px rgba(86, 189, 163, 0.22) !important;
}
.zone-header {
  background: linear-gradient(90deg, #1e3a34 0%, #56bda3 100%) !important;
  border-bottom: none !important;
}
.zone-title {
  color: #ffffff !important;
}
.zone-sub-title {
  border-bottom-color: #56bda3 !important;
}

/* LO Card Design (Instantly Distinguishable) */
.lo-card {
  border-left: 8px solid #edbe38 !important;
  border-right: 1px solid #e2e8f0 !important;
  border-top: 1px solid #e2e8f0 !important;
  border-bottom: 1px solid #e2e8f0 !important;
  background: linear-gradient(
    135deg,
    rgba(237, 190, 56, 0.12) 0%,
    rgba(255, 255, 255, 0.95) 100%
  ) !important;
  position: relative !important;
}
.lo-card:hover {
  border-color: #edbe38 !important;
  transform: translateY(-8px) scale(1.02) !important;
  box-shadow: 0 20px 40px rgba(237, 190, 56, 0.22) !important;
}
.lo-header {
  background: linear-gradient(90deg, #debe60 0%, #eab21b 100%) !important;
  border-bottom: none !important;
}
.lo-title {
  color: #ffffff !important;
}
.lo-sub-title {
  border-bottom-color: #edbe38 !important;
  color: #0a0f29 !important;
}
.white-text-compat {
  color: #0a0f29 !important;
}

/* Contribution Rows & Stat Boxes */
.zone-contribution-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: rgba(86, 189, 163, 0.08);
  border-left: 4px solid #56bda3;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
}
.zone-contribution-row:hover {
  background: rgba(86, 189, 163, 0.14);
  transform: translateX(4px);
}

.zone-stat-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
}
.zone-stat-box:hover {
  transform: translateX(4px);
}
.zone-stat-box.total {
  border-left: 4px solid #1e3a34;
}
.zone-stat-box.approved {
  border-left: 4px solid #56bda3;
}
.zone-stat-box.rejected {
  border-left: 4px solid #edbe38;
}

.lo-contribution-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: rgba(237, 190, 56, 0.08);
  border-left: 4px solid #edbe38;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
}
.lo-contribution-row:hover {
  background: rgba(237, 190, 56, 0.14);
  transform: translateX(4px);
}

.lo-stat-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
}
.lo-stat-box:hover {
  transform: translateX(4px);
}
.lo-stat-box.total {
  border-left: 4px solid #78350f;
}
.lo-stat-box.approved {
  border-left: 4px solid #56bda3;
}
.lo-stat-box.rejected {
  border-left: 4px solid #edbe38;
}

/* Badges for Zone and LO Total */
.stat-badge-zone-total {
  background-color: #1e3a34 !important;
  color: #ffffff !important;
  font-weight: 750;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 1.25rem;
  display: inline-block;
}
.stat-badge-lo-total {
  background-color: #78350f !important;
  color: #ffffff !important;
  font-weight: 750;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 1.25rem;
  display: inline-block;
}

/* Watermark icons */
.watermark-icon {
  position: absolute !important;
  right: -30px !important;
  bottom: -30px !important;
  font-size: 160px !important;
  pointer-events: none !important;
  z-index: 0 !important;
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
}

.zone-watermark {
  color: rgba(86, 189, 163, 0.08) !important;
}

.lo-watermark {
  color: rgba(237, 190, 56, 0.08) !important;
}

.zone-card:hover .zone-watermark {
  transform: scale(1.15) rotate(15deg) !important;
  color: rgba(86, 189, 163, 0.14) !important;
}

.lo-card:hover .lo-watermark {
  transform: scale(1.15) rotate(-15deg) !important;
  color: rgba(237, 190, 56, 0.14) !important;
}

.card-content-relative {
  position: relative !important;
  z-index: 1 !important;
}

/* Sub section titles */
.sub-section-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #0a0f29;
  margin-bottom: 12px;
  padding-bottom: 4px;
  border-bottom: 2.5px solid #3a67b1;
  display: inline-block;
  letter-spacing: 0.2px;
}

/* Info detail items */
.detail-item {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 6px;
  border: 1px solid #eef2f6;
  transition: all 0.2s ease;
  width: 100%;
}

.detail-item:hover {
  background: #f1f5f9;
  border-color: #3a67b1;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.detail-label {
  font-size: 1.2rem;
  font-weight: 700;
  color: #3a67b1;
  letter-spacing: 0.2px;
}

.detail-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: #0a0f29;
}

.detail-label-sub {
  font-size: 1.2rem;
  font-weight: 700;
  color: #414042;
}

.detail-value-highlight {
  font-size: 1.25rem;
  font-weight: 750;
  color: #0a0f29;
}

/* Badges for statistics */
.stat-badge-navy {
  background-color: #3a67b1 !important;
  color: #ffffff !important;
  font-weight: 750;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 1.25rem;
  display: inline-block;
}
.stat-badge-aqua {
  background-color: #0097d7 !important;
  color: #ffffff !important;
  font-weight: 750;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 1.25rem;
  display: inline-block;
}
.stat-badge-seaform {
  background-color: #56bda3 !important;
  color: #ffffff !important;
  font-weight: 750;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 1.25rem;
  display: inline-block;
}
.stat-badge-gold {
  background-color: #edbe38 !important;
  color: #0a0f29 !important;
  font-weight: 750;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 1.25rem;
  display: inline-block;
}

/* Chip override */
.chip-text-large {
  font-size: 1.2rem !important;
  font-weight: 700 !important;
  height: 36px !important;
}

.text-right-align {
  text-align: right;
  max-width: 60%;
  word-wrap: break-word;
}

.student-img {
  border-radius: 12px;
  border: 3px solid #3a67b1;
  box-shadow: 0 4px 12px rgba(10, 15, 41, 0.1);
  background: #f8fafc;
  padding: 4px;
  max-width: 160px;
  margin: 0 auto;
  transition: all 0.3s ease;
}
.student-img:hover {
  transform: scale(1.05);
  border-color: #0097d7;
}

/* Transaction Table styling */
.transaction-table {
  width: 100%;
  border-collapse: collapse;
}

.transaction-table th {
  background: #f8fafc !important;
  font-size: 1.2rem !important;
  font-weight: 700 !important;
  color: #0a0f29 !important;
  border-bottom: 2px solid #e2e8f0 !important;
  padding: 16px 20px !important;
  text-align: left;
}

.transaction-table td {
  font-size: 1.25rem !important;
  color: #414042 !important;
  border-bottom: 1px solid #edf2f7 !important;
  padding: 16px 20px !important;
}

.transaction-table tbody tr:hover {
  background-color: rgba(58, 103, 177, 0.04) !important;
}

/* Attachments */
.attachment-title {
  font-size: 1.2rem;
  font-weight: 750;
  color: #0a0f29;
  margin-bottom: 8px;
}

.img-container {
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #e2e8f0;
  background: #f8fafc;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.img-container:hover {
  border-color: #3a67b1;
  box-shadow: 0 8px 20px rgba(58, 103, 177, 0.15);
  transform: translateY(-3px);
}

.attachment-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: all 0.3s ease;
}

.download-link {
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(58, 103, 177, 0.08);
  transition: all 0.2s ease;
}

.download-link:hover {
  background: #3a67b1;
}

.download-link:hover .v-icon {
  color: #ffffff !important;
}

/* Description Text & Remarks */
.description-text {
  font-size: 1.25rem;
  line-height: 1.6;
  color: #414042;
}

.remarks-textarea {
  font-size: 1.25rem !important;
}
.remarks-textarea ::v-deep label {
  font-size: 1.2rem !important;
  color: #414042 !important;
}
.remarks-textarea ::v-deep textarea {
  font-size: 1.25rem !important;
  color: #0a0f29 !important;
  line-height: 1.5 !important;
}

/* Action Buttons */
.action-btn {
  font-size: 1.25rem !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  padding: 0 32px !important;
  height: 52px !important;
  border-radius: 26px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
}

.action-btn:hover {
  transform: translateY(-3px) scale(1.02) !important;
  box-shadow: 0 8px 20px rgba(10, 15, 41, 0.15) !important;
}

.off-black-text {
  color: #0a0f29 !important;
}

/* Animations keyframes */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUpDown {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideDownIn {
  from {
    opacity: 0;
    transform: translateY(-25px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-slide-down {
  opacity: 0;
  animation: slideDownIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-slide-up {
  opacity: 0;
  animation: slideUpDown 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.delay-1 {
  animation-delay: 0.15s;
}

.delay-2 {
  animation-delay: 0.3s;
}

.delay-3 {
  animation-delay: 0.45s;
}

.delay-4 {
  animation-delay: 0.6s;
}

/* Responsive Overrides */
@media (max-width: 960px) {
  .fill-height {
    height: auto;
  }
}

@media (max-width: 600px) {
  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  .text-right-align {
    text-align: left;
    max-width: 100%;
  }
  .header-title {
    font-size: 1.6rem;
  }
  .header-subtitle {
    font-size: 1rem;
  }
}
</style>
